import React, {useEffect, useState} from 'react'
import Searchbar from 'app/components/Searchbar'
import EcosystemIcon from 'app/components/EcosystemIcon'
import EmptyState from 'app/components/EmptyState'
import ExportButton from 'app/components/ExportButton'
import * as XLSX from 'xlsx'
import {
  showErrorToast,
  showSuccessToast,
  showLoadingToast,
  updateLoadingToast,
  dismissLoadingToast,
} from 'app/components/ShowToast'
import Button from 'app/components/Button'
import {useAuth} from 'app/modules/auth'
import cloudService from '../../services/cloud.services'

interface CloudData {
  id: string
  type: string
  purpose: string
  name: string
  account_id: string
  severity: string
  open_issues: string
  ignored: string
  last_scanned: any
  created_at: any
  scan_results?: ScanResult[]
}

interface ScanResult {
  category: string
  description: string
  message: string
  plugin: string
  resource: string
  status: string
  title: string
  regions?: string[]
}

interface CloudIssuesProps {
  cloud: CloudData
  onCloudUpdate?: (updatedCloud: CloudData) => void
}

const CloudIssues: React.FC<CloudIssuesProps> = ({cloud, onCloudUpdate}) => {
  const {currentUser} = useAuth()
  const [scanResults, setScanResults] = useState<ScanResult[]>([])
  const [filteredResults, setFilteredResults] = useState<ScanResult[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [isScanning, setIsScanning] = useState(false)

  useEffect(() => {
    if (cloud?.scan_results) {
      const results: ScanResult[] = []
      const scanResultsData = cloud.scan_results as any
      const excluded = (cloud as any)?.excluded_categories || []

      if (Array.isArray(scanResultsData)) {
        const visible = (scanResultsData as ScanResult[]).filter(
          (r) => !excluded.includes(r.category)
        )
        setScanResults(visible)
      } else {
        Object.keys(scanResultsData).forEach((key) => {
          if (key.startsWith('item_')) {
            const item = scanResultsData[key]
            if (item && typeof item === 'object') {
              if (item.item_0) {
                results.push(item.item_0 as ScanResult)
              } else {
                results.push(item as ScanResult)
              }
            }
          }
        })
        const visible = results.filter((r) => !excluded.includes(r.category))
        setScanResults(visible)
      }
    } else {
      setScanResults([])
    }
  }, [cloud])

  useEffect(() => {
    let filtered = scanResults
    filtered = filtered.filter((result) => {
      return result.status === 'FAIL'
    })

    if (searchTerm) {
      filtered = filtered.filter(
        (result) =>
          result.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          result.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          result.category.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    setFilteredResults(filtered)
  }, [scanResults, searchTerm])

  const handleStartScan = async () => {
    if (!currentUser?.tenantId) {
      showErrorToast({message: 'No tenant ID found'})
      return
    }
    const now = new Date()
    const lastScannedDate = cloud.last_scanned?.toDate
      ? cloud.last_scanned.toDate()
      : new Date(cloud.last_scanned)
    const twelveHoursAgo = new Date(now.getTime() - 12 * 60 * 60 * 1000)

    if (lastScannedDate && lastScannedDate > twelveHoursAgo) {
      const timeDiffMs = now.getTime() - lastScannedDate.getTime()
      const timeDiffHours = timeDiffMs / (1000 * 60 * 60)
      showErrorToast({
        message: `Scan for ${cloud.name} was run recently. Please wait ${Math.ceil(
          12 - timeDiffHours
        )} hours before scanning again.`,
      })
      return
    }
    setIsScanning(true)
    let toastId
    try {
      toastId = showLoadingToast({message: `Starting scan for ${cloud.name}...`})
      await new Promise((resolve) => setTimeout(resolve, 1000))

      let scanResults

      updateLoadingToast(toastId, {
        message: 'Running Cloud scan...',
        type: 'info',
        isLoading: true,
      })
      scanResults = await cloudService.runCloudSploitScanFromCloudId(currentUser.tenantId, cloud.id)

      if (scanResults && scanResults.success) {
        updateLoadingToast(toastId, {
          message: 'Scan completed, updating results...',
          type: 'info',
          isLoading: true,
        })

        const updatedCloud = await cloudService.getCloudById(currentUser.tenantId, cloud.id)
        if (updatedCloud && onCloudUpdate) {
          onCloudUpdate(updatedCloud as CloudData)
        }

        dismissLoadingToast(toastId)
        showSuccessToast({message: `Scan completed successfully for ${cloud.name}`})
      } else {
        dismissLoadingToast(toastId)
        let errorMessage = 'Please check your configuration.'
        if (scanResults?.error) {
          if (scanResults.error.includes('Credential validation failed:')) {
            errorMessage = scanResults.error.replace('Credential validation failed: ', '')
          } else {
            errorMessage = scanResults.error
          }
        }
        showErrorToast({message: `Scan failed for ${cloud.name}: ${errorMessage}`})
      }
    } catch (error: any) {
      dismissLoadingToast(toastId)

      let errorMessage = 'Unknown error'
      if (error.message) {
        if (error.message.includes('Credential validation failed:')) {
          errorMessage = error.message.replace('Credential validation failed: ', '')
        } else {
          errorMessage = error.message
        }
      }

      showErrorToast({message: `An error occurred while scanning ${cloud.name}: ${errorMessage}`})
    } finally {
      setIsScanning(false)
    }
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'ok':
        return 'badge-light-success'
      case 'fail':
        return 'badge-light-danger'
      case 'warning':
        return 'badge-light-warning'
      case 'info':
        return 'badge-light-info'
      default:
        return 'badge-light-primary'
    }
  }

  const exportData = () => {
    try {
      if (filteredResults.length === 0) {
        showErrorToast({message: 'No data to export.'})
        return
      }

      const workbook = XLSX.utils.book_new()

      const exportData = filteredResults.map((result) => ({
        'Cloud Type': cloud.type,
        'Cloud Name': cloud.name,
        'Account ID': cloud.account_id,
        'Issue Title': result.title,
        Description: result.description,
        Category: result.category,
        Regions: result.regions?.join(', '),
        Plugin: result.plugin,
        Status: result.status,
        Resource: result.resource,
        Message: result.message,
      }))

      const worksheet = XLSX.utils.json_to_sheet(exportData)

      worksheet['!cols'] = [
        {wch: 15},
        {wch: 20},
        {wch: 20},
        {wch: 30},
        {wch: 40},
        {wch: 15},
        {wch: 15},
        {wch: 20},
        {wch: 10},
        {wch: 25},
        {wch: 30},
      ]

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Cloud Issues')

      const currentDate = new Date().toISOString().split('T')[0]
      const filename = `${cloud.name || 'Cloud'}_Issues_${currentDate}.xlsx`

      XLSX.writeFile(workbook, filename)
      showSuccessToast({message: 'Cloud issues data exported successfully'})
    } catch (error) {
      showErrorToast({message: 'Failed to export data. Please try again.'})
    }
  }

  return (
    <div className='card'>
      <div className='card-header border-0 pt-6 h-100'>
        <div className='d-flex align-items-center justify-content-between w-100'>
          <div>
            <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          </div>
          <div className='d-flex gap-2'>
            <ExportButton onClick={() => exportData()} />
            <Button btnType='default' onClick={handleStartScan} disabled={isScanning}>
              {isScanning ? (
                <>
                  <span
                    className='spinner-border spinner-border-sm me-2'
                    role='status'
                    aria-hidden='true'
                  ></span>
                  Scanning...
                </>
              ) : (
                <>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-shield-search me-2'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M12 21a12 12 0 0 1 -8.5 -15a12 12 0 0 0 8.5 -3a12 12 0 0 0 8.5 3c.539 1.832 .627 3.747 .283 5.588' />
                    <path d='M18 18m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0' />
                    <path d='M20.2 20.2l1.8 1.8' />
                  </svg>
                  Start Scan
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      <div className='card-body py-3'>
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold fs-7' style={{color: '#5A5A5A'}}>
                <th className='ps-4 min-w-100px rounded-start'>TYPE</th>
                <th className='min-w-200px'>TITLE</th>
                <th className='min-w-150px'>DESCRIPTION</th>
                <th className='min-w-100px'>CATEGORY</th>
                <th className='min-w-100px'>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {filteredResults.length > 0 ? (
                filteredResults.map((result, index) => (
                  <tr
                    key={index}
                    className='border-bottom border-dotted'
                    style={{cursor: 'pointer'}}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}
                  >
                    <td>
                      <div className='d-flex align-items-center ps-4'>
                        <EcosystemIcon ecosystem={cloud.type} className='me-2' size={24} />
                      </div>
                    </td>
                    <td>
                      <div className='d-flex align-items-center'>
                        <span className='fw-semibold text-dark'>{result.title}</span>
                      </div>
                    </td>
                    <td>
                      <div className='d-flex align-items-center'>
                        <span className='text-gray-900 fs-7'>{result.description}</span>
                      </div>
                    </td>
                    <td>
                      <div className='d-flex align-items-center'>
                        {result.category ? (
                          <span className='badge badge-light-info fs-7 fw-bold'>
                            {result.category}
                          </span>
                        ) : (
                          <span className='text-muted fs-7'>--</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className='d-flex align-items-center'>
                        <span
                          className={`badge ${getStatusBadgeClass(result.status)} fs-7 fw-bold`}
                        >
                          {result.status}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      title={
                        scanResults.length === 0 ? 'No scan results available' : 'No issues found'
                      }
                      description={
                        scanResults.length === 0
                          ? "This cloud hasn't been scanned yet. Run a scan to see security issues and compliance results."
                          : 'No security issues with FAIL status were found in the scan results.'
                      }
                      isLoading={false}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default CloudIssues
